import { FC, useState, useEffect, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { TableLayout } from "Layouts";
import { ControlledTable, Filter2 } from "Components";
import { actionArr, FilterArr, TableCol } from "./Data";
import { RouteUrls } from "Constant/RouteUrls";
import { useDispatch, useSelector } from "react-redux";
import Formate from "./Formate";
import PatientApi from "Service/Patient.api";
import { addSelectedPrgm } from "Redux/Patient/Action";
import { ScreenInitialStateType, UserInitialStateType } from "DataTypes/Redux";
import { Filter2DataProps } from "DataTypes/Common";
import { selectedPrgmInitialState } from "Redux/Patient/reducer";
import { PendingProviderType } from "DataTypes/Services/Patient.type";
import { CreateFilter2Payload } from "Utils/common";
import CommonFormatter from "Utils/Common.Formatter";
import Stages from "Constant/Stages";
import PatientFormatter from "Utils/Patient.Formatter";
import { ReadOnlyType } from "DataTypes/PatientManagement.type";
import UI from "Constant/UI";
import { setPageLimit } from "Redux/Screen/Action";

const PendingProviderApproval: FC = () => {
  const permission: any = useSelector(
    (state: any) => state?.user?.myPermission?.pendingapprovepatient
  );
  const { myInfo }: UserInitialStateType = useSelector(
    (state: any) => state?.user
  );
  const { listPagination }: ScreenInitialStateType = useSelector(
    (state: any) => state?.screen
  );
  const reduxLimit = (listPagination || []).find(
    el => el.id == "pending-approval"
  )?.limit || 10;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [rows, setRows] = useState<PendingProviderType[]>([]);
  const [pageData, setPageData] = useState({
    ...UI.pageData,
    limit: reduxLimit
  });
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [filterHeight, setFilterHeight] = useState<number>(0);
  const [filterData, setFilterData] = useState<Filter2DataProps[]>(
    JSON.parse(JSON.stringify(FilterArr()))
  );
  const tableHeight = `calc(99% - ${filterHeight}px)`;

  useEffect(() => {
    handleSetData(1, pageData.limit);
  }, [filterData]);

  const handleSetData: (
    currentPage: number,
    currentLimit: number
  ) => void = async (currentPage, currentLimit) => {
    setLoading(true);
    const newFilterPayload = CreateFilter2Payload(filterData);
    const payload = {
      ...(newFilterPayload || {}),
    };
    const res = await PatientApi.PendingApproval({
      offset: (currentPage - 1) * currentLimit,
      limit: currentLimit,
      data: payload,
    });
    let newPageData = { ...UI.pageData }
    let newRows: PendingProviderType[] = [];
    if (res?.success) {
      newRows = Formate.PendingApprovalList(res?.data);
      newPageData = res.criterion;
    }
    setRows(newRows);
    setPageData(newPageData)
    dispatch(setPageLimit({
      id: "pending-approval",
      limit: newPageData.limit
    }));
    setLoading(false);
  };

  const handlePatientDetails: (data: PendingProviderType) => void = (data) => {
    navigate(
      `../../${RouteUrls.patient}/${data.patientId}/${RouteUrls.details}`
    );
  };

  const handleChart: (data: PendingProviderType) => void = (data) => {
    navigate(
      `../../${RouteUrls.patient}/${data.patientId}/` +
      `${RouteUrls.clinicalSummary}`
    );
  };

  const handleTask: (data: PendingProviderType) => void = (data) => {
    navigate(
      `../../${RouteUrls.patient}/${data.patientId}/` +
      `${RouteUrls.task}/${RouteUrls.add}`
    );
  };

  const handleCarePlan: (data: PendingProviderType) => void = (data) => {
    dispatch(
      addSelectedPrgm({
        ...selectedPrgmInitialState,
        readOnlyArr:
          data.careplanType == "REVISION"
            ? ["care-team", "editDevice"]
            : ["uptoDate"],
        ...(myInfo.roleId == 3 ? { showSaveBtn: true } : {}),
        ...(myInfo.roleId == 5 ? { showSubmitBtn: true } : {}),
        showLogTime: true,
        careplanType: data.careplanType,
        careplanId: data.careplanId,
        programId: data.programId,
        abbrevation: data.program,
        careplanState: Stages.CPSignOff,
        careplanStage: PatientFormatter.GetCPStage(Stages.CPSignOff),
        caremanagerId: data.caremanagerId,
        billableproviderId: data.billableproviderId,
        providerId: data.teamProviderId,
        teamInitiatorRoleId: 3,
        eligibleProgramId: data.eligibleProgramId,
      })
    );
    navigate(
      `../../${RouteUrls.patient}/${data.patientId
      }/${CommonFormatter.HandleParentRoute({
        prgm: data.program,
        isRev: data.careplanType == "REVISION",
        careId: data.careplanId,
      })}/${RouteUrls.careSummary}`
    );
  };

  const handleViewCare: (data: PendingProviderType) => void = (data) => {
    dispatch(
      addSelectedPrgm({
        ...selectedPrgmInitialState,
        careplanType: data.careplanType,
        careplanActivity: data.program,
        careplanId: data.careplanId,
        programId: data.programId,
        abbrevation: data.program,
        careplanState: Stages.CPSignOff,
        caremanagerId: data.caremanagerId,
        billableproviderId: data.billableproviderId,
        providerId: data.teamProviderId,
        careplanStage: PatientFormatter.GetCPStage(Stages.CPSignOff),
        readOnlyArr: [
          "uptoDate",
          "problem",
          "addDevice",
          "editDevice",
          "question",
          "addNotes",
          "frequency",
          "interactive",
          "medically",
          ...data.careplanType == "REVISION" ?
            [] :
            [
              "care-team",
              "add-provider",
              "edit-provider"
            ] as ReadOnlyType[],
        ],
      })
    );
    navigate(
      `../../${RouteUrls.patient}/${data.patientId
      }/${CommonFormatter.HandleParentRoute({
        prgm: data.program,
        isRev: data.careplanType == "REVISION",
        careId: data.careplanId,
      })}/${RouteUrls.careSummary}`
    );
  };

  const TblBodyJSX = (
    <ControlledTable.TblBody
      tableColumn={() => TableCol}
      tableRow={rows}
      tableAction={(data: PendingProviderType) =>
        actionArr(
          () => handleChart(data),
          () => handlePatientDetails(data),
          () => handleTask(data),
          () => handleCarePlan(data),
          () => handleViewCare(data),
          myInfo.roleId,
          data
        )
      }
      searchText={searchText}
    />
  );

  return (
    <Fragment>
      <Filter2
        filterDataArr={filterData}
        handleFilterClick={(data) => setFilterData(data)}
        changeSearchText={(val) => setSearchText(val)}
        setFilterHeight={(val) => setFilterHeight(val)}
      />
      {permission?.search && (
        <TableLayout
          tblBody={TblBodyJSX}
          tblHead={ControlledTable.TblHead(TableCol)}
          height={tableHeight}
          loading={loading}
          pageRecordCount={rows.length}
          totalRecordCount={pageData.total}
          Pagination={
            <ControlledTable.Pagination
              mainPage={pageData.page}
              limit={pageData.limit}
              total={pageData.total}
              handleClick={handleSetData}
              tableStyle={true}
            />
          }
        />
      )}
    </Fragment>
  );
};
export default PendingProviderApproval;
